/**
 * Achievement UI Module
 * Handles the achievements interface and user interactions
 */

import achievementEngine from './achievements.js';
import { getAchievementById, getAllAchievementIds, ACHIEVEMENT_CATEGORIES, ACHIEVEMENT_TIERS } from './achievementTypes.js';

/**
 * Achievement UI Manager
 */
class AchievementUI {
  constructor() {
    this.currentFilter = 'all';
    this.userAchievements = [];
    this.achievementStats = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the achievements UI
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    console.log('Initializing Achievement UI...');

    try {
      // Set up achievement unlock listener
      this.setupAchievementUnlockListener();

      // Set up progress update listener
      achievementEngine.addAchievementListener(this.onProgressUpdate.bind(this));

      this.isInitialized = true;
      console.log('Achievement UI initialized successfully');

    } catch (error) {
      console.error('Error initializing Achievement UI:', error);
    }
  }

  /**
   * Get all achievements including placeholders for ones not yet started
   */
  async getAllAchievementsWithProgress() {
    try {
      // Get user's actual progress
      const userAchievements = await achievementEngine.getUserAchievements();
      const userProgressMap = {};
      
      userAchievements.forEach(achievement => {
        userProgressMap[achievement.achievementId] = achievement;
      });

      // Get all available achievements and merge with user progress
      const allAchievementIds = getAllAchievementIds();
      const allAchievements = [];

      allAchievementIds.forEach(achievementId => {
        const achievementDef = getAchievementById(achievementId);
        if (achievementDef) {
          const userProgress = userProgressMap[achievementId];
          
          if (userProgress) {
            // User has progress data
            allAchievements.push({
              ...userProgress,
              definition: achievementDef
            });
          } else {
            // Create placeholder for achievement user hasn't started
            allAchievements.push({
              userId: achievementEngine.userId,
              achievementId,
              status: 'not_started',
              progress: {
                currentValue: 0,
                targetValue: achievementDef.requirements.threshold,
                percentage: 0,
                lastUpdated: new Date()
              },
              earnedAt: null,
              streakData: achievementDef.type === 'streak' ? {
                currentStreak: 0,
                bestStreak: 0,
                streakStartDate: null
              } : null,
              shareCount: 0,
              isHidden: false,
              metadata: {},
              definition: achievementDef
            });
          }
        }
      });

      return allAchievements;
    } catch (error) {
      console.error('Error getting all achievements with progress:', error);
      return [];
    }
  }

  /**
   * Load and display achievements for the current view
   */
  async loadAchievements() {
    console.log('Loading achievements...');

    try {
      // Show loading state
      this.showLoadingState();

      // Get user achievements and stats
      this.userAchievements = await this.getAllAchievementsWithProgress();
      this.achievementStats = await achievementEngine.getAchievementStats();

      // Update stats overview
      this.updateStatsOverview();

      // Render achievement gallery
      this.renderAchievementGallery();

      // Load recent achievements
      this.loadRecentAchievements();

      console.log('Achievements loaded successfully');

    } catch (error) {
      console.error('Error loading achievements:', error);
      this.showErrorState();
    }
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    const gallery = document.getElementById('achievement-gallery');
    if (gallery) {
      gallery.innerHTML = `
        <div class="achievement-loading">
          <div class="loading-spinner"></div>
          <p>Loading your achievements...</p>
        </div>
      `;
    }
  }

  /**
   * Show error state
   */
  showErrorState() {
    const gallery = document.getElementById('achievement-gallery');
    if (gallery) {
      gallery.innerHTML = `
        <div class="achievement-loading">
          <p>Unable to load achievements. Please try again later.</p>
        </div>
      `;
    }
  }

  /**
   * Update stats overview
   */
  updateStatsOverview() {
    const totalBadgesEl = document.getElementById('total-badges');
    const totalPointsEl = document.getElementById('total-points');
    const inProgressEl = document.getElementById('in-progress-count');

    if (totalBadgesEl) totalBadgesEl.textContent = this.achievementStats.totalBadges;
    if (totalPointsEl) totalPointsEl.textContent = this.achievementStats.totalPoints;
    if (inProgressEl) inProgressEl.textContent = this.achievementStats.inProgress;
  }

  /**
   * Render achievement gallery
   */
  renderAchievementGallery() {
    const gallery = document.getElementById('achievement-gallery');
    if (!gallery) return;

    // Filter achievements based on current filter
    const filteredAchievements = this.filterAchievements(this.userAchievements);

    // Create achievement grid
    const achievementGrid = document.createElement('div');
    achievementGrid.className = 'achievement-grid';

    if (filteredAchievements.length === 0) {
      achievementGrid.innerHTML = `
        <div class="achievement-loading">
          <p>No achievements found for this category.</p>
        </div>
      `;
    } else {
      filteredAchievements.forEach(achievement => {
        const card = this.createAchievementCard(achievement);
        achievementGrid.appendChild(card);
      });
    }

    gallery.innerHTML = '';
    gallery.appendChild(achievementGrid);
  }

  /**
   * Filter achievements based on current filter
   */
  filterAchievements(achievements) {
    if (this.currentFilter === 'all') {
      return achievements;
    }

    return achievements.filter(achievement => 
      achievement.definition.category === this.currentFilter
    );
  }

  /**
   * Create achievement card element
   */
  createAchievementCard(userAchievement) {
    const achievement = userAchievement.definition;
    const card = document.createElement('div');
    
    // Determine card state classes
    const statusClass = userAchievement.status === 'completed' ? 'completed' :
                       userAchievement.status === 'in_progress' ? 'in-progress' : 'locked';

    card.className = `achievement-card ${statusClass}`;
    card.dataset.achievementId = achievement.id;

    // Create badge icon
    const badgeIcon = this.getBadgeIcon(achievement);
    const badgeClass = userAchievement.status === 'completed' ? achievement.tier : 'locked';

    // Calculate progress percentage
    const progressPercentage = Math.min(userAchievement.progress.percentage, 100);

    // Get guidance text for locked/in-progress achievements
    const guidanceText = this.getAchievementGuidance(achievement, userAchievement);
    const guidanceSection = guidanceText ? `
      <div class="achievement-guidance">
        <div class="guidance-icon">💡</div>
        <div class="guidance-text">${guidanceText}</div>
      </div>
    ` : '';

    // Show different content based on status
    const progressSection = userAchievement.status === 'completed' ? `
      <div class="achievement-completed">
        <div class="completed-icon">✅</div>
        <div class="completed-text">Completed ${this.getTimeAgo(userAchievement.earnedAt)}!</div>
      </div>
    ` : `
      <div class="achievement-progress">
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${progressPercentage}%"></div>
        </div>
        <div class="progress-text">
          ${userAchievement.progress.currentValue} / ${userAchievement.progress.targetValue}
          (${progressPercentage.toFixed(1)}%)
        </div>
      </div>
    `;

    card.innerHTML = `
      <div class="achievement-header">
        <div class="achievement-badge ${badgeClass}">
          ${badgeIcon}
        </div>
        <div class="achievement-info">
          <h3>${achievement.name}</h3>
          <p>${achievement.description}</p>
        </div>
      </div>

      ${progressSection}
      ${guidanceSection}

      <div class="achievement-metadata">
        <span class="achievement-tier ${achievement.tier}">${achievement.tier}</span>
        <span class="achievement-points">+${achievement.rewards.points} pts</span>
      </div>
    `;

    // Add click handler for achievement details
    card.addEventListener('click', () => {
      this.showAchievementDetails(userAchievement);
    });

    return card;
  }

  /**
   * Get badge icon for achievement
   */
  getBadgeIcon(achievement) {
    // Map achievement categories to icons
    const categoryIcons = {
      budget_discipline: '💪',
      savings: '💰', 
      consistency: '📅',
      seasonal: '🎄',
      collaborative: '👥'
    };

    return categoryIcons[achievement.category] || '🏆';
  }

  /**
   * Get helpful guidance text for achievements
   */
  getAchievementGuidance(achievement, userAchievement) {
    if (userAchievement.status === 'completed') {
      return null; // No guidance needed for completed achievements
    }

    const progressData = userAchievement.progress;
    const remaining = progressData.targetValue - progressData.currentValue;

    // Provide specific guidance based on achievement type
    switch (achievement.id) {
      case 'budget_boss_1':
        return progressData.currentValue === 0 
          ? "Create a budget and spend less than your income for a full month to earn this badge!"
          : "Keep tracking your expenses and stay under budget to complete this achievement!";

      case 'budget_boss_3':
        return remaining === 3 
          ? "Stay under budget for 3 consecutive months. Start by completing one month!"
          : `${remaining} more consecutive months under budget to go!`;

      case 'budget_boss_6':
        return remaining === 6 
          ? "A challenging 6-month streak! Focus on building consistent budgeting habits."
          : `${remaining} more consecutive months to achieve this gold badge!`;

      case 'master_budgeter':
        return remaining === 12 
          ? "The ultimate budgeting achievement! Master your finances for a full year."
          : `${remaining} more months of budget discipline for this legendary achievement!`;

      case 'precision_spender_1':
        return "Try to finish the month within 5% of your total budget. Plan your spending carefully!";

      case 'budget_rookie':
        return "New to budgeting? Create your first budget and complete one month to get started!";

      case 'first_hundred_saved':
        return progressData.currentValue === 0 
          ? "Start saving by spending less than your budget allows. Every dollar counts!"
          : `$${(100 - progressData.currentValue).toFixed(2)} more to save for your first milestone!`;

      case 'savings_milestone_500':
        return `Save $${(500 - progressData.currentValue).toFixed(2)} more by staying under budget!`;

      case 'savings_milestone_1000':
        return `Save $${(1000 - progressData.currentValue).toFixed(2)} more to reach this major milestone!`;

      case 'daily_tracker_7':
        return progressData.currentValue === 0 
          ? "Start by logging your expenses every day. Use the expense tracker to build the habit!"
          : `${remaining} more consecutive days of expense logging to go!`;

      case 'daily_tracker_30':
        return `${remaining} more consecutive days of expense tracking for this silver badge!`;

      case 'organization_master':
        return "Make sure all your expenses are properly categorized. No 'uncategorized' expenses for a full month!";

      default:
        // Generic guidance based on achievement type
        if (achievement.type === 'streak') {
          return `Build a streak of ${achievement.requirements.threshold} ${achievement.requirements.timeframe === 'daily' ? 'days' : 'months'}!`;
        } else if (achievement.type === 'milestone') {
          return `Reach the target of ${achievement.requirements.threshold} to unlock this achievement!`;
        } else {
          return "Keep working towards this achievement by maintaining good financial habits!";
        }
    }
  }

  /**
   * Show achievement details with helpful information
   */
  showAchievementDetails(userAchievement) {
    const achievement = userAchievement.definition;
    const progress = userAchievement.progress;
    const remaining = progress.targetValue - progress.currentValue;
    
    let statusText = '';
    if (userAchievement.status === 'completed') {
      statusText = `✅ Completed ${this.getTimeAgo(userAchievement.earnedAt)}!`;
    } else if (userAchievement.status === 'in_progress') {
      statusText = `🔄 In Progress - ${remaining} more to go!`;
    } else {
      statusText = '🔒 Not started yet';
    }

    let detailsText = `${achievement.name}\n`;
    detailsText += `${achievement.tier.toUpperCase()} • ${achievement.category.replace('_', ' ').toUpperCase()}\n\n`;
    detailsText += `${achievement.description}\n\n`;
    detailsText += `Status: ${statusText}\n`;
    detailsText += `Progress: ${progress.currentValue} / ${progress.targetValue} (${progress.percentage.toFixed(1)}%)\n`;
    detailsText += `Points: +${achievement.rewards.points}\n\n`;

    // Add guidance for non-completed achievements
    const guidance = this.getAchievementGuidance(achievement, userAchievement);
    if (guidance) {
      detailsText += `💡 How to earn this:\n${guidance}\n\n`;
    }

    // Add streak information if applicable
    if (userAchievement.streakData) {
      detailsText += `Current streak: ${userAchievement.streakData.currentStreak}\n`;
      detailsText += `Best streak: ${userAchievement.streakData.bestStreak}\n\n`;
    }

    // Add tips based on achievement type
    const tips = this.getAchievementTips(achievement);
    if (tips) {
      detailsText += `💭 Tips:\n${tips}`;
    }

    alert(detailsText);
  }

  /**
   * Get helpful tips for specific achievements
   */
  getAchievementTips(achievement) {
    switch (achievement.category) {
      case 'budget_discipline':
        return "• Set realistic budgets\n• Review your spending weekly\n• Use the expense tracker daily\n• Look for areas to cut back";
        
      case 'savings':
        return "• Start with small amounts\n• Automate your savings\n• Track progress monthly\n• Celebrate milestones";
        
      case 'consistency':
        return "• Build daily habits\n• Set reminders\n• Use the mobile app\n• Track everything, even small expenses";
        
      default:
        return "• Stay consistent with your financial habits\n• Use all features of the app\n• Check your progress regularly";
    }
  }

  /**
   * Load recent achievements
   */
  async loadRecentAchievements() {
    try {
      const progressData = achievementEngine.progressData;
      const recentMilestones = progressData?.recentMilestones || [];

      const recentList = document.getElementById('recent-achievements-list');
      if (!recentList) return;

      if (recentMilestones.length === 0) {
        recentList.innerHTML = `
          <div class="no-recent-achievements">
            <p>Start budgeting to earn your first achievements!</p>
          </div>
        `;
        return;
      }

      // Create recent achievement items
      const recentHTML = recentMilestones.map(milestone => {
        const achievement = getAchievementById(milestone.achievementId);
        if (!achievement) return '';

        const badgeIcon = this.getBadgeIcon(achievement);
        const timeAgo = this.getTimeAgo(milestone.unlockedAt);

        return `
          <div class="recent-achievement-item">
            <div class="recent-achievement-badge ${milestone.tier}">
              ${badgeIcon}
            </div>
            <div class="recent-achievement-details">
              <h4>${milestone.achievementName}</h4>
              <p>Earned ${timeAgo} • +${milestone.points} points</p>
            </div>
          </div>
        `;
      }).filter(html => html).join('');

      recentList.innerHTML = recentHTML;

    } catch (error) {
      console.error('Error loading recent achievements:', error);
    }
  }

  /**
   * Get time ago string
   */
  getTimeAgo(timestamp) {
    const now = new Date();
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  }

  /**
   * Filter achievements by category
   */
  setFilter(category) {
    this.currentFilter = category;
    
    // Update active tab
    document.querySelectorAll('.category-tab').forEach(tab => {
      tab.classList.remove('active');
    });

    const activeTab = document.querySelector(`[data-category="${category}"]`);
    if (activeTab) {
      activeTab.classList.add('active');
    }

    // Re-render gallery with new filter
    this.renderAchievementGallery();
  }

  /**
   * Handle progress updates
   */
  onProgressUpdate(progressData) {
    console.log('Achievement progress updated:', progressData);
    
    // Reload achievements to reflect progress changes
    this.loadAchievements();
  }

  /**
   * Setup listener for achievement unlocks
   */
  setupAchievementUnlockListener() {
    if (typeof window === 'undefined') return;

    window.addEventListener('achievementUnlocked', (event) => {
      const { achievement } = event.detail;
      this.showUnlockNotification(achievement);
    });
  }

  /**
   * Show achievement unlock notification
   */
  showUnlockNotification(achievement) {
    console.log('🎉 Achievement Unlocked!', achievement.name);

    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'achievement-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-icon">🏆</div>
        <div class="notification-text">
          <h4>Achievement Unlocked!</h4>
          <p>${achievement.name}</p>
        </div>
      </div>
    `;

    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
      color: white;
      padding: 16px;
      border-radius: 12px;
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      animation: slideInRight 0.5s ease-out;
      max-width: 300px;
    `;

    // Add animation styles if not already present
    if (!document.getElementById('achievement-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'achievement-notification-styles';
      styles.textContent = `
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .achievement-notification .notification-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .achievement-notification .notification-icon {
          font-size: 24px;
        }
        .achievement-notification .notification-text h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }
        .achievement-notification .notification-text p {
          margin: 0;
          font-size: 12px;
          opacity: 0.9;
        }
      `;
      document.head.appendChild(styles);
    }

    // Add to page
    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.animation = 'slideInRight 0.5s ease-out reverse';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    }, 5000);

    // Add confetti effect
    this.createConfettiEffect();
  }

  /**
   * Create confetti effect for achievement unlock
   */
  createConfettiEffect() {
    const overlay = document.createElement('div');
    overlay.className = 'celebration-overlay';
    document.body.appendChild(overlay);

    // Create confetti pieces
    for (let i = 0; i < 50; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.backgroundColor = ['#667eea', '#764ba2', '#f39c12', '#27ae60', '#e74c3c'][Math.floor(Math.random() * 5)];
      confetti.style.animationDelay = Math.random() * 2 + 's';
      overlay.appendChild(confetti);
    }

    // Remove after animation
    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 3000);
  }

  /**
   * Cleanup when switching views
   */
  cleanup() {
    // Remove any notification elements
    document.querySelectorAll('.achievement-notification').forEach(el => el.remove());
    document.querySelectorAll('.celebration-overlay').forEach(el => el.remove());
  }
}

// Create singleton instance
const achievementUI = new AchievementUI();

export default achievementUI;

/**
 * Global functions for HTML onclick handlers
 */
export function filterAchievements(category) {
  achievementUI.setFilter(category);
}

export function initializeAchievementsView() {
  achievementUI.initialize();
  achievementUI.loadAchievements();
}

// Export for window access
if (typeof window !== 'undefined') {
  window.filterAchievements = filterAchievements;
  window.initializeAchievementsView = initializeAchievementsView;
}